import React from 'react'
import { GridColDef } from '@mui/x-data-grid-pro'
import { StringCell, StringProps } from 'components/Shared/MuiTable/Cell/StringCell'
import { TFunction } from 'i18next'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { CampaignSummaryTask } from 'state/campaigns'
import { ActionTextCell, ActionTextProps } from 'components/Shared/MuiTable/Cell/RouteLinkCell'
import { HistorySearchObject, SEARCH_PARAMS, getSearchParams } from 'components/utils/url'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { SubmissionStatusAvailability } from 'components/utils'

export interface SubmissionRowProps {
    id: string
    taskName: StringProps
    stepName: StringProps
    pending: ActionTextProps | StringProps
    forReview: ActionTextProps | StringProps
    editsRequested: ActionTextProps | StringProps
    completed: ActionTextProps | StringProps
    totalSubmissions: StringProps
}

export const getColumns = (t: TFunction): GridColDef[] => {
    const cols: GridColDef[] = [
        {
            field: 'taskName',
            type: 'string',
            headerName: t('submission.table.taskNameLabel'),
            width: 244,
            renderCell: StringCell,
            sortable: false,
            filterable: false,
        },
        {
            field: 'stepName',
            type: 'string',
            headerName: t('submission.table.stepNameLabel'),
            width: 255,
            renderCell: StringCell,
            sortable: false,
            filterable: false,
        },
        {
            field: 'pending',
            type: 'string',
            headerName: t('statusLabel.pending'),
            width: 200,
            renderCell: params => (params.value.url ? <ActionTextCell {...params} /> : <StringCell {...params} />),
            sortable: false,
            filterable: false,
            renderHeader: getInfoTooltipHeaderCell({
                width: 255,
                description: t('statusLabel.pendingSubmissionTooltipDescription'),
                options: {
                    mr: 0,
                },
            }),
        },
        {
            field: 'forReview',
            type: 'string',
            headerName: t('statusLabel.review'),
            width: 200,
            renderCell: params => (params.value.url ? <ActionTextCell {...params} /> : <StringCell {...params} />),
            sortable: false,
            filterable: false,
            renderHeader: getInfoTooltipHeaderCell({
                width: 255,
                description: t('statusLabel.reviewTooltipDescription'),
                options: {
                    mr: 0,
                },
            }),
        },
        {
            field: 'editsRequested',
            type: 'string',
            headerName: t('statusLabel.editsRequested'),
            width: 200,
            renderCell: params => (params.value.url ? <ActionTextCell {...params} /> : <StringCell {...params} />),
            sortable: false,
            filterable: false,
            renderHeader: getInfoTooltipHeaderCell({
                width: 255,
                description: t('statusLabel.editRequestTooltipDescription'),
                options: {
                    mr: 0,
                },
            }),
        },
        {
            field: 'completed',
            type: 'string',
            headerName: t('statusLabel.completed'),
            width: 200,
            renderCell: params => (params.value.url ? <ActionTextCell {...params} /> : <StringCell {...params} />),
            sortable: false,
            filterable: false,
            renderHeader: getInfoTooltipHeaderCell({
                width: 255,
                description: t('statusLabel.completedTooltipDescription'),
                options: {
                    mr: 0,
                },
            }),
        },
        {
            field: 'totalSubmissions',
            type: 'string',
            headerName: t('submission.table.totalSubmissionsForStepLabel'),
            width: 213,
            renderCell: StringCell,
            sortable: false,
            filterable: false,
            renderHeader: getInfoTooltipHeaderCell({
                width: 255,
                description: t('submission.table.totalSubmissionsForStepTooltip'),
                options: {
                    mr: 0,
                },
            }),
        },
    ]
    return cols
}

const getRowValue = (value: StringProps['value']) => ({
    value,
    isFullText: true,
})

// stepName cell using numbered text, utilizing html list to help with numbered cell value
const getStepNameCellElement = (value: string, index: number) => {
    return (
        <ol start={index} style={{ margin: 0, paddingLeft: 20 }}>
            <li>{value}</li>
        </ol>
    )
}

interface GetSubmissionStatusCell {
    text: string
    searchParams: HistorySearchObject[]
    viewType: SUBMISSION_VIEW_ENUM
    isStatusExist: boolean
}

const getSubmissionStatusCell = ({ text, searchParams, viewType, isStatusExist }: GetSubmissionStatusCell) => {
    if (!isStatusExist) {
        return getRowValue('N/A')
    }

    const params = getSearchParams({
        add: [...searchParams, { key: SEARCH_PARAMS.viewType, value: viewType }],
    })
    return {
        actionTitle: text,
        url: params,
        dataTestId: 'test-action-text-cell-' + viewType,
    }
}

export const getRows = (
    tasks: CampaignSummaryTask[],
    statusConfig: SubmissionStatusAvailability,
): SubmissionRowProps[] => {
    const sortedTasks = tasks.sort((taskA, taskB) => taskA.ordering_index - taskB.ordering_index)
    const rows: SubmissionRowProps[] = sortedTasks
        .map(task => {
            const taskListTransformed = task.steps.map((step, stepIndex) => {
                const { pending_count, for_review_count, edits_requested_count, completed_count } =
                    step.submission_progress_summary
                const total = pending_count + for_review_count + edits_requested_count + completed_count
                const stepNumber = stepIndex + 1
                const stepNameElement = getStepNameCellElement(step.title, stepNumber)
                const searchParams = [
                    { key: SEARCH_PARAMS.step, value: step.uuid },
                    { key: SEARCH_PARAMS.task, value: task.uuid },
                ]
                const isPendingExist = !!statusConfig[SUBMISSION_VIEW_ENUM.PENDING][step.type]
                const isForReviewExist = !!statusConfig[SUBMISSION_VIEW_ENUM.FOR_REVIEW][step.type]
                const isEditRequestedExist = !!statusConfig[SUBMISSION_VIEW_ENUM.EDITS_REQUESTED][step.type]
                const isCompletedExist = !!statusConfig[SUBMISSION_VIEW_ENUM.COMPLETED][step.type]

                return {
                    id: step.uuid,
                    taskName: getRowValue(stepIndex === 0 ? task.title : ' '),
                    stepName: getRowValue(stepNameElement),
                    pending: getSubmissionStatusCell({
                        isStatusExist: isPendingExist,
                        text: pending_count.toString(),
                        searchParams,
                        viewType: SUBMISSION_VIEW_ENUM.PENDING,
                    }),
                    forReview: getSubmissionStatusCell({
                        isStatusExist: isForReviewExist,
                        text: for_review_count.toString(),
                        searchParams,
                        viewType: SUBMISSION_VIEW_ENUM.FOR_REVIEW,
                    }),
                    editsRequested: getSubmissionStatusCell({
                        isStatusExist: isEditRequestedExist,
                        text: edits_requested_count.toString(),
                        searchParams,
                        viewType: SUBMISSION_VIEW_ENUM.EDITS_REQUESTED,
                    }),
                    completed: getSubmissionStatusCell({
                        isStatusExist: isCompletedExist,
                        text: completed_count.toString(),
                        searchParams,
                        viewType: SUBMISSION_VIEW_ENUM.COMPLETED,
                    }),
                    totalSubmissions: getRowValue(total.toString()),
                }
            })
            return [...taskListTransformed]
        })
        .flat(1)
    return rows
}
